import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactHeader from "./reactHeader";
import FooterSection from "./footerSection";
import AnimatedBlock from "./animatedBlock";
import "./auth.css";
import { msalInstance } from "./msalInstance";
import {
  loginRequest,
  NEW_USER_REDIRECT_PATH,
  EXISTING_USER_REDIRECT_PATH,
} from "./authConfig";

// Holds an access code from the link (e.g. /auth?accessCode=ROB2026) across
// the full-page redirect to B2C and back, since neither React state nor
// react-router survive that round trip.
const LINK_ACCESS_CODE_STORAGE_KEY = "khor_link_access_code";

const AuthSection = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const startedRef = useRef(false);
  // B2C sends the user back with `code`/`error` in the URL hash, so that's
  // present only on the return trip — lets us show the right loading
  // message instead of "Redirecting you to sign in…" the whole time.
  const [isReturning] = useState(
    () => /[#&](code|error)=/.test(window.location.hash)
  );

  useEffect(() => {
    // Guards against React StrictMode's double-invoked effects in dev —
    // only the first live run should kick off loginRedirect.
    if (startedRef.current) return;
    startedRef.current = true;

    const run = async () => {
      try {
        await msalInstance.initialize();
        const response = await msalInstance.handleRedirectPromise();

        if (response) {
          if (response.account) {
            // So other pages (e.g. /request-access) can read claims off
            // msalInstance.getActiveAccount() without re-deriving them.
            msalInstance.setActiveAccount(response.account);
          }

          // Retrieve the link's access code (if any) and clear it
          // immediately so it can never carry into a later sign-in.
          const linkAccessCode = sessionStorage.getItem(LINK_ACCESS_CODE_STORAGE_KEY);
          sessionStorage.removeItem(LINK_ACCESS_CODE_STORAGE_KEY);

          const destination =
            response.idTokenClaims?.newUser === true
              ? NEW_USER_REDIRECT_PATH
              : EXISTING_USER_REDIRECT_PATH;
          navigate(destination, { replace: true, state: { accessCode: linkAccessCode } });
          return;
        }

        // No redirect response to process — this is the initial click,
        // so send the user to B2C to sign in or sign up. Forward an
        // access code from the link (e.g. /auth?accessCode=ROB2026) so B2C
        // can attach it to the account. Named `accessCode`, not `code`, so
        // it can't collide with the OAuth authorization `code` param.
        const accessCode = new URLSearchParams(window.location.search).get("accessCode");
        if (accessCode) {
          sessionStorage.setItem(LINK_ACCESS_CODE_STORAGE_KEY, accessCode);
        }
        const request = accessCode
          ? { ...loginRequest, extraQueryParameters: { accessCode } }
          : loginRequest;
        await msalInstance.loginRedirect(request);
      } catch (err) {
        console.error("B2C sign-in failed:", err);
        setError(
          err?.errorCode === "user_cancelled"
            ? "Sign-in was cancelled."
            : "We couldn't complete sign-in."
        );
      }
    };

    run();
  }, [navigate]);

  if (error) {
    return (
      <section className="page-section section-style-contact auth-status-section">
        <AnimatedBlock>
          <div className="auth-status-content">
            <div className="auth-status-icon auth-status-icon--error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="13" />
                <line x1="12" y1="16" x2="12" y2="16" />
              </svg>
            </div>
            <h1 className="auth-status-heading">{error}</h1>
            <a href="/" className="installation-link auth-home-link">
              Return to homepage
            </a>
          </div>
        </AnimatedBlock>
      </section>
    );
  }

  return (
    <section className="page-section section-style-contact auth-status-section">
      <AnimatedBlock>
        <div className="auth-status-content">
          <div className="auth-spinner" aria-hidden="true" />
          <p className="auth-status-body">
            {isReturning ? "Signing you in…" : "Redirecting you to sign in…"}
          </p>
        </div>
      </AnimatedBlock>
    </section>
  );
};

const Auth = () => (
  <div className="App">
    <Helmet>
      <title>Sign In | Khor Developer</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <ReactHeader />
    <AuthSection />
    <FooterSection />
  </div>
);

export default Auth;
